import { useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useDispatch, useSelector } from 'react-redux'
import { goBack } from '@slices/uiSlice'
import { soundManager } from '@core/audio/sounds/soundManager'
import { resolveHowToPlaySteps } from './howToPlayContent'
import { renderVisual } from './visuals'

const slideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 44 : -44, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction > 0 ? -44 : 44, opacity: 0 }),
}

const HowToPlay = () => {
  const dispatch = useDispatch()
  const reduce = useReducedMotion()

  const gameCode = useSelector((state) => state.launchParams.params.gameCode)
  const { family, steps } = resolveHowToPlaySteps(gameCode)
  const total = steps.length

  // [index, direction] — direction feeds the slide variants.
  const [[index, direction], setStep] = useState([0, 0])
  const current = steps[index]
  const isLast = index === total - 1

  const goTo = (nextIndex, nextDirection) => {
    soundManager.play('CLICK')
    setStep([nextIndex, nextDirection])
  }

  const handleNext = () => {
    if (isLast) {
      soundManager.play('CLICK')
      dispatch(goBack())
      return
    }
    goTo(index + 1, 1)
  }

  const handleBack = () => {
    if (index > 0) goTo(index - 1, -1)
  }

  const transition = { duration: reduce ? 0 : 0.22, ease: 'easeOut' }

  return (
    <div className="standard-normal-how-to-play-container">
      <div className="standard-normal-how-to-play-dots">
        {steps.map((step, dotIndex) => (
          <button
            key={step.no}
            type="button"
            aria-label={`Go to step ${dotIndex + 1}`}
            aria-current={dotIndex === index}
            className={`standard-normal-how-to-play-dot ${dotIndex === index ? 'is-active' : ''}`}
            onClick={() => goTo(dotIndex, dotIndex > index ? 1 : -1)}
          />
        ))}
      </div>

      <div className="standard-normal-how-to-play-stage">
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={current.no}
            className="standard-normal-how-to-play-step"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={transition}
          >
            <div className="standard-normal-how-to-play-visual">
              {renderVisual(current.visual, family)}
            </div>
            <div className="standard-normal-how-to-play-copy">
              <span className="standard-normal-how-to-play-step-label">
                Step {current.no} of {total}
              </span>
              <h3 className="standard-normal-how-to-play-heading">{current.heading}</h3>
              <p className="standard-normal-how-to-play-body">{current.body}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="standard-normal-how-to-play-footer">
        <button
          type="button"
          className={`standard-normal-how-to-play-nav is-ghost ${index === 0 ? 'is-hidden' : ''}`}
          onClick={handleBack}
        >
          Back
        </button>
        <span className="standard-normal-how-to-play-counter">
          {index + 1} / {total}
        </span>
        <button
          type="button"
          className={`standard-normal-how-to-play-nav ${isLast ? 'is-done' : 'is-primary'}`}
          onClick={handleNext}
        >
          {isLast ? 'Got it' : 'Next'}
        </button>
      </div>
    </div>
  )
}

export default HowToPlay
